import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ReactSVG from 'react-svg';
import { TourProvider as CoreProvider, useTour } from '@reactour/tour';
import CardActions from '@material-ui/core/CardActions';
import Button from '@material-ui/core/Button';
import * as lib from '~/dependencies';
import { PropTypes, useTranslation } from '~/dependencies';
import { Settings as SettingsIcon, MessagesIcon } from '~/assets/svgIcons';
import * as actionTypes from '~/store/actions/actionTypes';
import { Card, CardHeader } from './styles';

function ContentComponent(props) {
  const { steps: stepsAux, currentStep, setCurrentStep } = props;
  const curStepInfo = stepsAux[currentStep];
  const {
    title,
    content,
    buttons,
    iconSource,
    styles,
    isFinished,
  } = curStepInfo;

  const { setIsOpen } = useTour();

  const handleClose = () => {
    // If last step or first step
    if (currentStep === 0) {
      setIsOpen(false);
    } else if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleNext = () => {
    setCurrentStep(currentStep + 1);
    if (isFinished) {
      setIsOpen(false);
    }
  };

  return (
    <Card>
      <CardHeader
        avatar={
          <div
            className={`tour-provider-card-header-avatar ${styles.cardHeaderAvatar}`}>
            <ReactSVG src={iconSource} />
          </div>
        }
        title={title}
        subheader={content}
      />
      <CardActions
        disableSpacing
        className="tour-provider-card-actions-container">
        <Button className="actions-container-button" onClick={handleClose}>
          {buttons.close}
        </Button>
        <Button
          className="actions-container-button next-button"
          onClick={handleNext}>
          {buttons.next}
        </Button>
      </CardActions>
    </Card>
  );
}

ContentComponent.propTypes = {
  currentStep: PropTypes.string.isRequired,
  steps: PropTypes.instanceOf(Object).isRequired,
  setCurrentStep: PropTypes.func.isRequired,
};

export default function TourProvider({ children }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const isTourCompleted = useSelector(
    state => state?.accountInfo?.tourCompleted
  );

  const [steps, setSteps] = useState([]);

  const getTourSteps = useCallback(() => {
    return [
      {
        selector: '.settings-react-tour',
        title: t('components.tourProvider.steps.settingsReactTour.title'),
        content: t('components.tourProvider.steps.settingsReactTour.content'),
        buttons: {
          next: t(
            'components.tourProvider.steps.settingsReactTour.buttons.next'
          ),
          close: t(
            'components.tourProvider.steps.settingsReactTour.buttons.close'
          ),
        },
        isFinished: false,
        iconSource: SettingsIcon,
        styles: {
          cardHeaderAvatar: 'step-1',
        },
      },
      {
        selector: '.conversations-react-tour',
        title: t('components.tourProvider.steps.conversationsReactTour.title'),
        content: t(
          'components.tourProvider.steps.conversationsReactTour.content'
        ),
        buttons: {
          next: t(
            'components.tourProvider.steps.conversationsReactTour.buttons.next'
          ),
          close: t(
            'components.tourProvider.steps.conversationsReactTour.buttons.close'
          ),
        },
        isFinished: true,
        iconSource: MessagesIcon,
        styles: {
          cardHeaderAvatar: 'step-2',
        },
      },
    ];
  }, [t]);

  useEffect(() => {
    if (!isTourCompleted) {
      setSteps(getTourSteps());
    }
  }, [getTourSteps, isTourCompleted]);

  useEffect(() => {
    const handleLanguageChange = () => {
      if (!isTourCompleted) {
        setSteps(getTourSteps());
      }
    };

    lib.i18n.on('languageChanged', handleLanguageChange);

    return () => {
      lib.i18n.off('languageChanged', handleLanguageChange);
    };
  }, [getTourSteps, isTourCompleted]);

  const handleBeforeClose = () => {
    dispatch({
      type: actionTypes.ACCOUNT_INFO_SET_TOUR_COMPLETED,
      payload: true,
    });
  };

  return (
    <CoreProvider
      key={JSON.stringify(steps)}
      steps={steps}
      showBadge={false}
      showCloseButton={false}
      beforeClose={handleBeforeClose}
      ContentComponent={ContentComponent}
      styles={{
        popover: (base, { horizontalAlign }) => {
          return {
            ...base,
            padding: 0,
            borderRadius: 20,
            ...(horizontalAlign === 'right' && {
              left: 'auto',
            }),
          };
        },
        maskWrapper: base => ({
          ...base,
        }),
        highlightedArea: base => ({
          ...base,
          display: 'block',
          pointerEvents: 'none',
        }),
        maskArea: (base, { x }) => {
          return {
            ...base,
            rx: 30,
            ...(x === 0
              ? {
                  x: 5,
                  y: 2,
                  width: 55,
                  height: 55,
                }
              : {
                  width: 41,
                  height: 41,
                }),
          };
        },
      }}>
      {children}
    </CoreProvider>
  );
}

TourProvider.propTypes = {
  children: PropTypes.instanceOf(Object).isRequired,
};
